import { getTtsCapability, normalizeSpeechText, startSpeech, stopActiveSpeech } from './ttsRuntime.js';

export const LESSON_TTS_CHANNEL = 'assessnow-lesson-tts';
const MAX_MESSAGE_CHARACTERS = 2000;
const MAX_SESSION_CHARACTERS = 30000;
const MAX_REQUESTS = 300;

export function createLessonTtsHost({ host = window, frame, session, capability = getTtsCapability,
  speak = startSpeech, stop = stopActiveSpeech } = {}) {
  let closed = false;
  let activeRequest = null;
  let characters = 0;
  let requests = 0;
  let generation = 0;
  const target = () => frame?.contentWindow;
  const reply = (requestId, event, detail = {}) => {
    if (closed) return;
    try { target()?.postMessage({ channel: LESSON_TTS_CHANNEL, version: 1, session, requestId, event, ...detail }, '*'); }
    catch { /* Lesson closure may remove the frame before a speech callback. */ }
  };
  const cancel = reason => {
    activeRequest = null;
    generation += 1;
    stop(reason, host);
  };
  const onMessage = message => {
    if (closed || !target() || message.source !== target() || message.origin !== 'null') return;
    const data = message.data;
    if (!data || data.channel !== LESSON_TTS_CHANNEL || data.version !== 1 || data.session !== session
      || !Number.isSafeInteger(data.requestId) || data.requestId < 0) return;
    if (data.action === 'capability') {
      const state = capability(host);
      reply(data.requestId, 'capability', { available: Boolean(state.available), engine: state.engine || 'unavailable' });
      return;
    }
    if (data.action === 'stop') {
      cancel('lesson-stop');
      reply(data.requestId, 'stopped');
      return;
    }
    if (data.action !== 'speak') return;
    const raw = data.text;
    const text = typeof raw === 'string' ? normalizeSpeechText(raw) : '';
    if (!text || raw.length > MAX_MESSAGE_CHARACTERS || ++requests > MAX_REQUESTS
      || characters + text.length > MAX_SESSION_CHARACTERS) {
      reply(data.requestId, 'error', { code: 'limit', message: 'This lesson has reached its read-aloud limit.' });
      return;
    }
    characters += text.length;
    cancel('lesson-next');
    activeRequest = data.requestId;
    const currentGeneration = generation;
    const same = () => !closed && generation === currentGeneration && activeRequest === data.requestId;
    const state = capability(host);
    if (!state.available) {
      activeRequest = null;
      reply(data.requestId, 'error', { code: 'unavailable', message: state.managedErrors
        ? 'Tablet speech needs setup. Ask your teacher to check the tablet speech settings.'
        : state.message || 'Read aloud is unavailable. You can continue reading the visible text.' });
      return;
    }
    try {
      speak({ text, host, rate: data.rate, language: 'en-US',
        onStart: () => { if (same()) reply(data.requestId, 'start'); },
        onBoundary: boundary => { if (same()) reply(data.requestId, 'boundary', {
          charIndex: Math.max(0, Math.min(text.length, Math.trunc(Number(boundary?.charIndex) || 0))),
          charLength: Math.max(0, Math.min(text.length, Math.trunc(Number(boundary?.charLength) || 0)))
        }); },
        onEnd: () => { if (same()) { activeRequest = null; reply(data.requestId, 'end'); } },
        onError: error => { if (same()) { activeRequest = null; reply(data.requestId, 'error', {
          code: String(error?.code || 'speech-error').slice(0, 64),
          message: state.managedErrors ? 'Tablet speech could not start. Ask your teacher to check its settings.'
            : 'Audio could not finish. Continue reading the visible text.'
        }); } }
      });
    } catch (error) {
      if (same()) { activeRequest = null; reply(data.requestId, 'error', {
        code: String(error?.code || 'speech-error').slice(0, 64),
        message: 'Audio could not start. Continue reading the visible text.'
      }); }
    }
  };
  const onHidden = () => { if (host.document?.hidden) cancel('lesson-background'); };
  host.addEventListener('message', onMessage);
  host.document?.addEventListener('visibilitychange', onHidden);
  const onPageHide = () => cancel('lesson-background');
  host.addEventListener('pagehide', onPageHide);
  return () => {
    if (closed) return;
    cancel('lesson-closed');
    closed = true;
    host.removeEventListener('message', onMessage);
    host.document?.removeEventListener('visibilitychange', onHidden);
    host.removeEventListener('pagehide', onPageHide);
  };
}
